import type { EntityATI } from '@atlassian/search-client/common/types';

import type { ProductKeys } from '../product-keys';

import type { BooleanFilterState, CreatedBooleanFilter } from './boolean-filter/types';
import type { CreatedSelectFilter, SelectFilterState } from './select-filter/types';
import type { NounSubtypeKey, TypeFilterValueKey } from './universal-type';

import type { FilterId } from './index';

export const CUSTOM_OPTION_VALUE = 'custom';
export const universalTypeFilterId = 'universalTypeFilter';

export type FilterControlDropdown = 'dropdown';
export type FitlerControlSort = 'sort';
export type FilterControlId = FilterControlDropdown | FitlerControlSort;
export type FilterOrControlId = FilterId | FilterControlId;

/**
 * Minimal filter shape needed for query generation (SSR-safe).
 * FilterUtils and allFilterData both satisfy this contract.
 * If FilterUtils starts reading additional fields, add them here —
 * this will force allFilterData to be updated at compile time.
 */
export type MinimalFilterValue = {
	readonly id: string;
	readonly products: readonly string[];
	readonly universal: boolean;
	readonly queryParams: {
		readonly key: string;
		readonly getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => BooleanFilterState | SelectFilterState;
	};
	readonly isSuggestedFilter?: (primaryProduct?: string) => boolean;
};

// TODO rename this so it makes more sense
export type AllFilterValues =
	| CreatedBooleanFilter<'watchedByMe'>
	| CreatedSelectFilter<'assignee'>
	| CreatedSelectFilter<'reporter'>
	| CreatedBooleanFilter<'commentedByMe'>
	| CreatedSelectFilter<'createdBy'>
	| CreatedSelectFilter<'content'>
	| CreatedSelectFilter<'contributor'>
	| CreatedSelectFilter<'lastUpdated'>
	| CreatedSelectFilter<'project'>
	| CreatedSelectFilter<'space'>
	| CreatedSelectFilter<'site'>
	| CreatedBooleanFilter<'verified'>
	| CreatedBooleanFilter<'onlyTitleMatch'>
	| CreatedSelectFilter<'universalTypeFilter'>
	| CreatedSelectFilter<'focusAreaType'>
	| CreatedSelectFilter<'focusAreaUnder'>
	| CreatedSelectFilter<'owner'>
	| CreatedSelectFilter<'label'>
	| CreatedSelectFilter<'pageStatus'>
	| CreatedSelectFilter<'lastUpdatedBy'>
	| CreatedBooleanFilter<'archived'>
	| CreatedSelectFilter<'statusFilter'>
	| CreatedBooleanFilter<'compassArchived'>
	| CreatedSelectFilter<'assetsSchema'>
	| CreatedSelectFilter<'assetsType'>
	| CreatedSelectFilter<'positionFocusArea'>
	| CreatedSelectFilter<'positionStatus'>
	| CreatedSelectFilter<'positionJobFamily'>
	| CreatedSelectFilter<'positionTeam'>
	| CreatedSelectFilter<'positionEmploymentType'>
	| CreatedSelectFilter<'sort'>
	| CreatedSelectFilter<'campaignStatus'>
	| CreatedSelectFilter<'caseStatus'>
	| CreatedSelectFilter<'documentStatus'>
	| CreatedSelectFilter<'calendarType'>
	| CreatedSelectFilter<'leadStatus'>
	| CreatedSelectFilter<'taskStatus'>
	| CreatedSelectFilter<'taskType'>
	| CreatedSelectFilter<'salesforceStage'>
	| CreatedSelectFilter<'repository'>
	| CreatedSelectFilter<'reviewer'>
	| CreatedSelectFilter<'pullRequestStatus'>
	| CreatedSelectFilter<'slackChannel'>
	| CreatedSelectFilter<'folder'>
	| CreatedSelectFilter<'workspaceName'>
	| CreatedBooleanFilter<'hasAttachment'>;

export type AllFilterStates =
	| BooleanFilterState<'watchedByMe'>
	| SelectFilterState<'assignee'>
	| SelectFilterState<'reporter'>
	| BooleanFilterState<'commentedByMe'>
	| SelectFilterState<'createdBy'>
	| SelectFilterState<'content'>
	| SelectFilterState<'contributor'>
	| SelectFilterState<'lastUpdated'>
	| SelectFilterState<'project'>
	| SelectFilterState<'space'>
	| SelectFilterState<'site'>
	| BooleanFilterState<'verified'>
	| BooleanFilterState<'onlyTitleMatch'>
	| SelectFilterState<'universalTypeFilter'>
	| SelectFilterState<'focusAreaType'>
	| SelectFilterState<'focusAreaUnder'>
	| SelectFilterState<'owner'>
	| SelectFilterState<'label'>
	| SelectFilterState<'pageStatus'>
	| SelectFilterState<'lastUpdatedBy'>
	| BooleanFilterState<'archived'>
	| SelectFilterState<'statusFilter'>
	| BooleanFilterState<'compassArchived'>
	| SelectFilterState<'assetsSchema'>
	| SelectFilterState<'assetsType'>
	| SelectFilterState<'positionFocusArea'>
	| SelectFilterState<'positionStatus'>
	| SelectFilterState<'positionJobFamily'>
	| SelectFilterState<'positionTeam'>
	| SelectFilterState<'positionEmploymentType'>
	| SelectFilterState<'sort'>
	| SelectFilterState<'campaignStatus'>
	| SelectFilterState<'caseStatus'>
	| SelectFilterState<'documentStatus'>
	| SelectFilterState<'calendarType'>
	| SelectFilterState<'leadStatus'>
	| SelectFilterState<'taskStatus'>
	| SelectFilterState<'taskType'>
	| SelectFilterState<'salesforceStage'>
	| SelectFilterState<'repository'>
	| SelectFilterState<'reviewer'>
	| SelectFilterState<'pullRequestStatus'>
	| SelectFilterState<'slackChannel'>
	| SelectFilterState<'folder'>
	| SelectFilterState<'workspaceName'>
	| BooleanFilterState<'hasAttachment'>;

// TODO: filterID opportunity
export type Filters = Record<string, AllFilterStates | undefined>;
export type AllFilterKeys = FilterId;

export type CreateFilters = SelectFilterState<FilterId> | BooleanFilterState<FilterId>;
export type SelectFilters = Record<FilterId, CreateFilters>;

export interface TypeFilterMapping {
	products: ProductKeys[];
	entities: Partial<Record<ProductKeys, EntityATI[]>>;
	subCategories: NounSubtypeKey[];
}

export type UniversalTypeFilterMapping = Record<TypeFilterValueKey, TypeFilterMapping>;
